import type { BugReportContext } from '../types';
import type { NavigationEntry, ApiRequestEntry, ConsoleEntry, MediaCaptureEntry } from '@/infrastructure/bug-report/types';

interface BuildContextParams {
  tenant: { uuid: string; name: string };
  user: { uuid: string; email: string; roleCode: string; roleName: string };
  navigationHistory: NavigationEntry[];
  apiRequests: ApiRequestEntry[];
  consoleLogs: ConsoleEntry[];
  mediaCaptures: MediaCaptureEntry[];
}

export function buildContextPayload(params: BuildContextParams): BugReportContext {
  return {
    version: '1.1',
    capturedAt: new Date().toISOString(),
    tenant: params.tenant,
    user: params.user,
    browser: {
      userAgent: navigator.userAgent,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    },
    navigationHistory: params.navigationHistory,
    apiRequests: params.apiRequests,
    consoleLogs: params.consoleLogs,
    mediaCaptures: params.mediaCaptures,
  };
}

export function buildFormData(
  formValues: {
    title: string;
    description: string;
    reason: string;
    areaPath: string;
    priority: number;
    /** Optional initial assignee email — only sent by users who can assign. */
    assignedTo?: string;
    /** Optional iteration path (Azure DevOps System.IterationPath). */
    iterationPath?: string;
  },
  context: BugReportContext,
  screenshot: File | null,
  additionalImages: File[],
  attachedFiles: File[] = []
): FormData {
  const formData = new FormData();

  // Form fields
  formData.append('title', formValues.title);
  formData.append('description', formValues.description);
  formData.append('reason', formValues.reason);
  formData.append('areaPath', formValues.areaPath);
  formData.append('priority', String(formValues.priority));
  if (formValues.assignedTo) {
    formData.append('assignedTo', formValues.assignedTo);
  }
  if (formValues.iterationPath) {
    formData.append('iterationPath', formValues.iterationPath);
  }
  formData.append('reporterEmail', context.user.email);
  formData.append('reporterRole', context.user.roleName);
  formData.append('tenantName', context.tenant.name);

  // Context JSON file
  const contextBlob = new Blob([JSON.stringify(context, null, 2)], { type: 'application/json' });
  formData.append('contextFile', contextBlob, `context-${Date.now()}.json`);

  // Screenshot
  if (screenshot) {
    formData.append('screenshot', screenshot);
  }

  // Additional images
  additionalImages.forEach((image) => {
    formData.append('images', image);
  });

  // Attached files (xlsx, csv, pdf, etc.)
  attachedFiles.forEach((file) => {
    formData.append('files', file);
  });

  return formData;
}
